import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLang } from '../App';
import { LanguageSelector } from '../components/LanguageSelector';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { LogoFull } from '../components/Logo';

const HERO_IMAGE = 'https://images.pexels.com/photos/1769540/pexels-photo-1769540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';

const GatewayPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, toggleLang } = useLang();
  const [loaded, setLoaded] = useState(false);
  const isEn = lang !== 'fr';

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, [user, navigate]);

  return (
    <div className="min-h-screen w-full overflow-x-hidden" data-testid="gateway-page">
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
        .font-cormorant { font-family: 'Cormorant Garamond', Georgia, serif; }
        .font-ibm { font-family: 'IBM Plex Sans', -apple-system, sans-serif; }
      `}</style>

      {/* HERO SECTION — full viewport height */}
      <section className="h-screen w-full grid grid-cols-1 lg:grid-cols-12">
        {/* Left Panel — Image */}
        <div className="hidden lg:block lg:col-span-7 relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[2s] ease-out"
            style={{
              backgroundImage: `url(${HERO_IMAGE})`,
              transform: loaded ? 'scale(1)' : 'scale(1.05)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10" />

          {/* Stats on image */}
          <div
            className="absolute bottom-0 left-0 right-0 p-12 xl:p-16 transition-all duration-[1.4s] ease-out"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(30px)',
            }}
          >
            <div className="flex items-end gap-16 mb-8">
              <div>
                <div className="font-cormorant text-6xl xl:text-7xl font-semibold text-white leading-none">27</div>
                <div className="font-ibm text-xs uppercase tracking-[0.2em] text-white/60 mt-2">
                  {isEn ? 'Development Banks' : 'Banques de Développement'}
                </div>
              </div>
              <div>
                <div className="font-cormorant text-6xl xl:text-7xl font-semibold text-white leading-none">$900B+</div>
                <div className="font-ibm text-xs uppercase tracking-[0.2em] text-white/60 mt-2">
                  {isEn ? 'Annual Commitments' : 'Engagements Annuels'}
                </div>
              </div>
            </div>
            <div className="font-ibm text-sm text-white/50 leading-relaxed max-w-lg">
              {isEn
                ? 'Aligned with the United Nations Sustainable Development Goals and the Paris Agreement on Climate.'
                : 'Aligné sur les Objectifs de Développement Durable des Nations Unies et l\'Accord de Paris sur le climat.'}
            </div>
          </div>
        </div>

        {/* Right Panel — Gateway */}
        <div className="col-span-1 lg:col-span-5 bg-white flex flex-col relative">
          {/* Language selector */}
          <div className="flex justify-end p-6 lg:p-8">
            <LanguageSelector tone="muted" testId="gateway-lang-selector" />
          </div>

          {/* Center Content */}
          <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-12 xl:px-16">
            <div
              className="transition-all duration-[1.2s] ease-out"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0)' : 'translateY(20px)',
              }}
            >
              {/* Brand */}
              <div className="mb-12">
                <LogoFull className="h-16 sm:h-20 mb-5 shadow-md" data-testid="gateway-logo" />
                <div className="font-ibm text-[11px] uppercase tracking-[0.25em] text-slate-400">
                  Co-Financing Platform
                </div>
              </div>

              {/* Tagline */}
              <p className="font-ibm text-base sm:text-lg text-slate-500 font-light leading-relaxed mb-4 max-w-sm" data-testid="gateway-tagline">
                {isEn
                  ? 'The institutional co-financing gateway for the International Development Finance Club.'
                  : 'Le portail institutionnel de co-financement de l\'International Development Finance Club.'}
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-3 mb-12">
                {[
                  isEn ? '27 MEMBERS' : '27 MEMBRES',
                  isEn ? 'SDGs ALIGNED' : 'ODD',
                  isEn ? 'PARIS AGREEMENT' : 'ACCORD DE PARIS',
                ].map((badge) => (
                  <span
                    key={badge}
                    className="font-ibm text-[10px] uppercase tracking-[0.15em] text-slate-400 border border-slate-200 px-3 py-1.5"
                    data-testid={`badge-${badge.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 max-w-sm">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-[#0F172A] text-white hover:bg-[#1E293B] py-5 px-8 transition-colors duration-300 w-full flex justify-between items-center group"
                  data-testid="gateway-signin-btn"
                >
                  <span className="font-ibm text-sm font-medium tracking-wide uppercase">
                    {isEn ? 'Sign In' : 'Se Connecter'}
                  </span>
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => navigate('/request-access')}
                  className="bg-transparent border border-slate-200 text-[#0A0A0A] hover:border-[#0F172A] hover:bg-slate-50 py-5 px-8 transition-all duration-300 w-full flex justify-between items-center group"
                  data-testid="gateway-register-btn"
                >
                  <span className="font-ibm text-sm font-medium tracking-wide uppercase">
                    {isEn ? 'Request Access' : 'Demander un Accès'}
                  </span>
                  <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>

              {/* Scroll hint */}
              <button
                onClick={() => {
                  const el = document.getElementById('how-it-works');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="font-ibm text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-[#0F172A] mt-10 flex items-center gap-2 group"
                data-testid="gateway-scroll-how"
              >
                {isEn ? 'See how it works' : 'Découvrez comment ça marche'}
                <span className="inline-block transition-transform group-hover:translate-y-0.5">↓</span>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 lg:p-8 flex items-center justify-between gap-3 flex-wrap">
            <p className="font-ibm text-[11px] text-slate-300" data-testid="gateway-footer">
              {isEn
                ? 'Secure access for IDFC network members.'
                : 'Accès sécurisé pour les membres du réseau IDFC.'}
            </p>
            <button
              onClick={() => navigate('/trust')}
              className="font-ibm text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-[#0F172A] transition-colors"
              data-testid="gateway-trust-link"
            >
              {isEn ? 'Trust & Security ↗' : 'Confiance & Sécurité ↗'}
            </button>
          </div>

          {/* Mobile stats — show on mobile only */}
          <div className="lg:hidden bg-[#0F172A] px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-cormorant text-3xl font-semibold text-white">27</div>
                <div className="font-ibm text-[10px] uppercase tracking-[0.15em] text-white/50">
                  {isEn ? 'Banks' : 'Banques'}
                </div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="font-cormorant text-3xl font-semibold text-white">$900B+</div>
                <div className="font-ibm text-[10px] uppercase tracking-[0.15em] text-white/50">
                  {isEn ? 'Annual' : 'Annuel'}
                </div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="font-cormorant text-3xl font-semibold text-white">17</div>
                <div className="font-ibm text-[10px] uppercase tracking-[0.15em] text-white/50">
                  {isEn ? 'SDGs' : 'ODD'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section
        id="how-it-works"
        className="bg-slate-50 py-20 sm:py-28 px-6 sm:px-10"
        data-testid="gateway-how-it-works"
      >
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="font-ibm text-[11px] uppercase tracking-[0.3em] text-[#0F172A]/60 mb-4">
              {isEn ? 'How it works' : 'Comment ça marche'}
            </div>
            <h2 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0A0A0A] leading-tight mb-5">
              {isEn
                ? 'From raw complexity to bankable clarity.'
                : 'De la complexité brute à la clarté bancable.'}
            </h2>
            <p className="font-ibm text-base sm:text-lg text-slate-500 font-light max-w-3xl mx-auto leading-relaxed">
              {isEn
                ? 'IDFC transforms scattered project documents into structured, decision-ready systems — so investment committees decide faster, with better data.'
                : 'IDFC transforme des documents de projet épars en systèmes structurés prêts pour la décision — afin que les comités d\'investissement tranchent plus vite, avec de meilleures données.'}
            </p>
          </div>

          {/* Infographic */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)] p-4 sm:p-6 lg:p-8 mb-12">
            <img
              src="/assets/how-it-works.png"
              alt={isEn ? 'How IDFC works: raw project chaos transformed into a structured, decision-ready system' : 'Comment fonctionne IDFC : le chaos des projets bruts transformé en système structuré et prêt pour la décision'}
              className="w-full h-auto"
              loading="lazy"
              data-testid="how-it-works-image"
            />
          </div>

          {/* 4 Value pillars */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            {[
              {
                fr: { t: 'Gagnez du Temps', d: 'Automatisez l\'analyse et le reporting.' },
                en: { t: 'Save Time', d: 'Automate analysis and reporting.' },
              },
              {
                fr: { t: 'Améliorez la Précision', d: 'Réduisez erreurs et données manquantes.' },
                en: { t: 'Improve Accuracy', d: 'Reduce errors and missing data.' },
              },
              {
                fr: { t: 'Meilleures Décisions', d: 'Insights data-driven à portée de main.' },
                en: { t: 'Better Decisions', d: 'Data-driven insights at your fingertips.' },
              },
              {
                fr: { t: 'Bancabilité Accrue', d: 'Dossiers projets plus solides, prêts investisseurs.' },
                en: { t: 'Increase Bankability', d: 'Stronger, investor-ready project files.' },
              },
            ].map((pillar, i) => {
              const content = isEn ? pillar.en : pillar.fr;
              return (
                <div
                  key={i}
                  className="bg-white border border-slate-200 p-6 sm:p-7 hover:border-[#0F172A] transition-colors"
                  data-testid={`pillar-${i}`}
                >
                  <div className="font-cormorant text-3xl font-semibold text-[#0F172A] mb-2">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="font-ibm text-sm font-medium text-[#0A0A0A] mb-2">{content.t}</div>
                  <div className="font-ibm text-xs text-slate-500 leading-relaxed">{content.d}</div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => navigate('/request-access')}
              className="bg-[#0F172A] text-white hover:bg-[#1E293B] py-4 px-10 transition-colors duration-300 inline-flex items-center gap-3 group"
              data-testid="how-it-works-cta"
            >
              <span className="font-ibm text-sm font-medium tracking-wide uppercase">
                {isEn ? 'Request Access' : 'Demander un Accès'}
              </span>
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GatewayPage;
